import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import {
  FaArrowLeft,
  FaBuilding,
  FaEnvelope,
  FaHeart,
  FaMapMarkerAlt,
  FaPhone,
  FaRegHeart,
  FaUser,
  FaSearch,
  FaTimes,
  FaEye,
  FaStar,
} from "react-icons/fa";
import { BsChat, BsChevronLeft, BsChevronRight } from "react-icons/bs";
import { BiCart } from "react-icons/bi";
import axios from "axios";
import { API_URL, IMAGE_PATH, APP_LOGO } from "../config/AppConfig";
import "../assets/css/Home.scss";

const categoryShowcase = [
  {
    id: "electronics",
    name: "Electronics",
    description: "Latest gadgets & consoles",
    image: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "fashion",
    name: "Fashion",
    description: "Streetwear & couture drops",
    image: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=900&q=80",
  },
  {
    id: "sneakers",
    name: "Sneakers",
    description: "Limited collaborations & grails",
    image: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=900&q=80",
  },
];

const heroFallbacks = [
  {
    banner_id: "default-1",
    fallbackImage: "https://images.unsplash.com/photo-1511556670061-7f302af04116?auto=format&fit=crop&w=1600&q=80",
    title: "TechXchange",
  },
  {
    banner_id: "default-2",
    fallbackImage: "https://images.unsplash.com/photo-1475180098004-ca77a66827be?auto=format&fit=crop&w=1600&q=80",
    title: "Marketplace",
  },
];

const Home = () => {
  const token = sessionStorage.getItem("token");
  const isLoggedIn = !!token;
  const navigate = useNavigate();

  const [banners, setBanners] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productLimit, setProductLimit] = useState(12);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showOwnerDetails, setShowOwnerDetails] = useState(false);
  const [likedProducts, setLikedProducts] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");

  const userId = sessionStorage.getItem("id");

  const fetchGuestProducts = () => {
    setLoading(true);
    axios
      .get(`${API_URL}/get_product_images`)
      .then((response) => {
        if (response.data.success) {
          setProducts(response.data.result || []);
        }
      })
      .catch((error) => console.error("Error:", error))
      .finally(() => setLoading(false));
  };

  const fetchAuthenticatedView = () => {
    setLoading(true);
    axios
      .get(`${API_URL}/get_home_page_api`, {
        params: { user_id: userId, product_limit: productLimit },
      })
      .then((response) => {
        if (response.data.success) {
          console.log("response.data.banner_arr", response.data);
          const bannerArr = response.data.banner_arr || [];
          const productArr = response.data.product_arr || [];

          setBanners(bannerArr);
          setProducts(productArr);

          const initialLikes = new Set();
          productArr.forEach((product) => {
            if (product.like_status === 1) {
              initialLikes.add(product.product_id);
            }
          });
          setLikedProducts(initialLikes);
        }
      })
      .catch((error) => console.error("Error:", error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchAuthenticatedView();
    } else {
      fetchGuestProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, productLimit, userId]);

  useEffect(() => {
    const total = banners.length || heroFallbacks.length;
    if (!total) {
      setCurrentBanner(0);
      return undefined;
    }

    setCurrentBanner((prev) => prev % total);

    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % total);
    }, 5500);

    return () => clearInterval(timer);
  }, [banners.length]);

  const handlePrevBanner = () => {
    const total = banners.length || heroFallbacks.length;
    if (!total) return;
    setCurrentBanner((prev) => (prev - 1 + total) % total);
  };

  const handleNextBanner = () => {
    const total = banners.length || heroFallbacks.length;
    if (!total) return;
    setCurrentBanner((prev) => (prev + 1) % total);
  };

  const handleLoadMore = () => setProductLimit((prev) => prev + 8);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleChatWithSeller = (product) => {
    console.log("handleChatWithSeller called with product:", product);
    
    if (!isLoggedIn) {
      console.log("User not logged in, redirecting to login");
      navigate("/login");
      return;
    }

    if (!userId) {
      console.error("User ID not found");
      alert("Unable to start chat: Please login again");
      return;
    }

    // Try to get seller ID from multiple possible field names
    const sellerId = product?.user_id || product?.owner_id || product?.seller_id;
    
    if (!sellerId) {
      console.error("Seller ID not found. Product data:", product);
      alert("Unable to start chat: Seller information not available");
      return;
    }

    if (!product?.product_id) {
      console.error("Product ID not found. Product data:", product);
      alert("Unable to start chat: Product information not available");
      return;
    }

    const roomId = `chat_${product.product_id}_${userId}_${sellerId}`;
    console.log("Navigating to chat room:", roomId);
    
    try {
      navigate(`/chat/${roomId}`, {
        state: {
          productId: product.product_id,
          buyerId: userId,
          sellerId: sellerId,
          productName: product.product_name || "Product",
          buyerName: sessionStorage.getItem("owner_name") || "You",
        },
      });
    } catch (error) {
      console.error("Error navigating to chat:", error);
      alert("Unable to start chat. Please try again.");
    }
  };

  const handleSearchClear = () => {
    setSearchTerm("");
  };

  const toggleLike = (productId, event) => {
    if (event) event.stopPropagation();
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
    if (!userId) return;

    const willLike = !likedProducts.has(productId);

    setLikedProducts((prev) => {
      const updated = new Set(prev);
      if (willLike) {
        updated.add(productId);
      } else {
        updated.delete(productId);
      }
      return updated;
    });

    axios
      .post(
        `${API_URL}/like_unlike_product`,
        {
          user_id: userId,
          product_id: productId,
          like_status: willLike ? 1 : 0,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .catch((error) => {
        console.error("Error toggling like:", error);
        // revert optimistic update
        setLikedProducts((prev) => {
          const reverted = new Set(prev);
          if (willLike) {
            reverted.delete(productId);
          } else {
            reverted.add(productId);
          }
          return reverted;
        });
      });
  };

  const handleProductClick = (product) => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
    setSelectedProduct(product);
    setShowOwnerDetails(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToList = () => {
    setSelectedProduct(null);
    setShowOwnerDetails(false);
  };

  const breadcrumbItems = useMemo(
    () => [
      { label: "Home / ", path: isLoggedIn ? "/" : "/login" },
      {
        label: selectedProduct ? selectedProduct.product_name : "All Product",
        path: "",
      },
    ],
    [isLoggedIn, selectedProduct]
  );

  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) return products;
    const term = searchTerm.toLowerCase();
    return products.filter(
      (p) =>
        p.product_name?.toLowerCase().includes(term) ||
        p.category_name?.toLowerCase().includes(term) ||
        p.brand_name?.toLowerCase().includes(term) ||
        p.description?.toLowerCase().includes(term)
    );
  }, [products, searchTerm]);

  if (loading) {
    return (
      <div className="page-loading">
        <div className="page-loading__indicator spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="page-loading__label">Getting everything ready...</p>
      </div>
    );
  }

  if (selectedProduct) {
    return (
      <ProductDetailsView
        product={selectedProduct}
        liked={likedProducts.has(selectedProduct.product_id)}
        onLike={toggleLike}
        onBack={handleBackToList}
        onToggleOwner={() => setShowOwnerDetails((prev) => !prev)}
        showOwnerDetails={showOwnerDetails}
        breadcrumbItems={breadcrumbItems}
        token={token}
        onChatWithSeller={handleChatWithSeller}
      />
    );
  }

  const shouldRenderFooter = !isLoggedIn;

  return (
    <div className="home-page">
      <main>
        {token && (
          <HeroSection
            banners={banners}
            currentIndex={currentBanner}
            onPrev={handlePrevBanner}
            onNext={handleNextBanner}
          />
        )}

        <SearchPanel
          value={searchTerm}
          onChange={handleSearchChange}
          onSubmit={handleSearchSubmit}
          onClear={handleSearchClear}
          tags={categoryShowcase.map((cat) => cat.name)}
        />

        <ProductSection
          products={filteredProducts}
          isLoggedIn={isLoggedIn}
          likedProducts={likedProducts}
          onLike={toggleLike}
          onSelect={handleProductClick}
          onLoadMore={handleLoadMore}
          searchTerm={searchTerm}
        />

        {/* Image Showcase Slider */}
        {filteredProducts.length > 0 && (
          <ImageShowcaseSlider products={filteredProducts} />
        )}
      </main>

      {shouldRenderFooter && <Footer token={token} />}
    </div>
  );
};

const HeroSection = ({ banners, currentIndex, onPrev, onNext }) => {
  const hasBanners = banners && banners.length > 0;
  const slides = hasBanners ? banners : heroFallbacks;
  const activeSlide = slides[currentIndex] || slides[0];

  const resolveImage = (slide) => {
    if (!slide) return heroFallbacks[0].fallbackImage;
    if (slide.image) return `${IMAGE_PATH}${slide.image}`;
    return slide.fallbackImage || heroFallbacks[0].fallbackImage;
  };

  return (
    <section className="hero hero--banner-only">
      <div className="container">
        <div className="hero-banner">
          <img src={resolveImage(activeSlide)} alt={activeSlide?.title || "Featured banner"} />
          {(activeSlide?.title || activeSlide?.description) && (
            <div className="hero-content">
              {activeSlide?.title && <h1>{activeSlide.title}</h1>}
              {activeSlide?.description && <p>{activeSlide.description}</p>}
            </div>
          )}
          {slides.length > 1 && (
            <div className="hero-banner__controls">
              <button type="button" onClick={onPrev} aria-label="Previous banner">
                <BsChevronLeft />
              </button>
              <button type="button" onClick={onNext} aria-label="Next banner">
                <BsChevronRight />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const SearchPanel = ({ value, onChange, onSubmit, onClear, tags }) => (
  <section className="search-panel">
    <div className="container">
      <div className="search-panel__inner">
        <div className="search-panel__copy">
          <h2>Find Your Perfect Match</h2>
          <p>Search through thousands of verified listings</p>
        </div>

        <form className="search-panel__form" onSubmit={onSubmit}>
          <div className="search-panel__field">
            <i><FaSearch /></i>
            <input
              type="text"
              value={value}
              onChange={onChange}
              placeholder="Search products, brands, categories..."
            />
            {value && (
              <button
                type="button"
                className="search-panel__clear"
                onClick={onClear}
                aria-label="Clear search"
              >
                <FaTimes />
              </button>
            )}
          </div>
        </form>

        <div className="search-panel__tags">
          {tags.map((tag) => (
            <button
              key={tag}
              type="button"
              className="search-tag"
              onClick={() => onChange({ target: { value: tag } })}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const ProductSection = ({
  products,
  isLoggedIn,
  likedProducts,
  onLike,
  onSelect,
  onLoadMore,
  searchTerm,
}) => (
  <section className="section">
    <div className="container">
      <div className="section-heading">
        <div className="section-heading__top">
          <div className="section-heading__bar"></div>
          <h2>Featured Products</h2>
        </div>
        <p className="section-heading__meta">{products.length} items available</p>
      </div>

      {products.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon">
            <FaSearch />
          </div>
          <h3>No products found</h3>
          <p>Try adjusting your search terms or browse all categories</p>
          {searchTerm && (
            <button className="btn btn-primary" onClick={() => { }}>
              Clear Search
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="category-explorer__grid">
            {products.map((product) => (
              <ProductCard
                key={product.product_id}
                product={product}
                liked={likedProducts.has(product.product_id)}
                onLike={onLike}
                onClick={() => onSelect(product)}
                isLoggedIn={isLoggedIn}
              />
            ))}
          </div>

          {/* Load More Button */}
          <div className="load-more-section">
            <button className="btn btn-primary btn-lg" onClick={onLoadMore}>
              Load more products
            </button>
          </div>
        </>
      )}
    </div>
  </section>
);



const ImageShowcaseSlider = ({ products }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Get all product images
  const allImages = useMemo(() => {
    const images = [];
    products.forEach((product) => {
      const rawImages = product.images;
      const normalizedImages = Array.isArray(rawImages)
        ? rawImages
        : typeof rawImages === "string" && rawImages !== "NA"
        ? [{ image: rawImages }]
        : [];

      if (normalizedImages.length > 0) {
        normalizedImages.forEach((img) => {
          if (!img?.image || img.image === "NA") return;
          images.push({
            url: img.image.startsWith("http") ? img.image : `${IMAGE_PATH}${img.image}`,
            productName: product.product_name,
            category: product.category_name,
          });
        });
      } else if (product.image && product.image !== "NA") {
        images.push({
          url: product.image.startsWith("http") ? product.image : `${IMAGE_PATH}${product.image}`,
          productName: product.product_name,
          category: product.category_name,
        });
      }
    });
    return images;
  }, [products]);

  const totalSlides = Math.ceil(allImages.length / 3);
  const hasMultipleSlides = allImages.length > 3;

  const handlePrev = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const getCurrentImages = () => {
    const startIndex = currentSlide * 3;
    return allImages.slice(startIndex, startIndex + 3);
  };

  if (allImages.length === 0) return null;

  return (
    <section className="image-showcase-section">
      <div className="container">
        <div className="section-heading">
          <div className="section-heading__top">
            <div className="section-heading__bar"></div>
            <h2>Product Gallery</h2>
          </div>
          <p className="section-heading__meta">
            {allImages.length} image{allImages.length !== 1 ? "s" : ""} from our collection
          </p>
        </div>

        <div className="image-showcase">
          <div className="image-showcase__grid">
            {getCurrentImages().map((image, index) => (
              <div key={`${currentSlide}-${index}`} className="image-showcase__item">
                <img src={image.url} alt={image.productName} />
                <div className="image-showcase__overlay">
                  <span className="image-showcase__category">{image.category}</span>
                  <h4 className="image-showcase__title">{image.productName}</h4>
                </div>
              </div>
            ))}
          </div>

          {hasMultipleSlides && (
            <div className="image-showcase__controls">
              <button
                type="button"
                onClick={handlePrev}
                className="image-showcase__btn image-showcase__btn--prev"
                aria-label="Previous images"
              >
                <BsChevronLeft />
              </button>
              <div className="image-showcase__dots">
                {Array.from({ length: totalSlides }).map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`image-showcase__dot ${index === currentSlide ? "is-active" : ""}`}
                    onClick={() => setCurrentSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={handleNext}
                className="image-showcase__btn image-showcase__btn--next"
                aria-label="Next images"
              >
                <BsChevronRight />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export const ProductCard = ({ product, liked, onLike, onClick, isLoggedIn }) => {
  const firstImageEntry = Array.isArray(product.images)
    ? product.images.find((img) => img?.image && img.image !== "NA")
    : typeof product.images === "string" && product.images !== "NA"
    ? { image: product.images }
    : null;

  const imageUrl = firstImageEntry
    ? firstImageEntry.image.startsWith("http")
      ? firstImageEntry.image
      : `${IMAGE_PATH}${firstImageEntry.image}`
    : product.image && product.image !== "NA"
    ? product.image.startsWith("http")
      ? product.image
      : `${IMAGE_PATH}${product.image}`
    : "https://via.placeholder.com/400x300?text=No+Image";

  return (
    <div className="product-card-modern" onClick={onClick}>
      <div className="product-card-modern__media">
        <img src={imageUrl} alt={product.product_name} />
        <span className="product-card-modern__pill">{product.category_name}</span>
        {isLoggedIn && (
          <button
            className={`product-card-modern__like ${liked ? "is-liked" : ""}`}
            onClick={(e) => onLike(product.product_id, e)}
            aria-label={liked ? "Unlike" : "Like"}
          >
            {liked ? <FaHeart /> : <FaRegHeart />}
          </button>
        )}
      </div>

      <div className="product-card-modern__body">
        <span className="product-card-modern__brand">{product.brand_name}</span>
        <h3>{product.product_name}</h3>
        <p>{product.description}</p>
      </div>

      <div className="product-card-modern__footer">
        <div className="product-card-modern__price">
          <small>AED</small> {product.price}
        </div>
        <button className="product-card-modern__view-btn" aria-label="View product">
          <FaEye />
        </button>
      </div>
    </div>
  );
};

export const ProductDetailsView = ({
  product,
  liked,
  onLike,
  onBack,
  onToggleOwner,
  showOwnerDetails,
  breadcrumbItems,
  token,
  onChatWithSeller,
}) => {
  const shouldShowFooter = !token;
  const galleryImages = Array.isArray(product.images)
    ? product.images.filter((img) => img?.image && img.image !== "NA")
    : typeof product.images === "string" && product.images !== "NA"
    ? [{ image: product.images }]
    : [];

  return (
    <div className="product-detail-page">
      <div className="container">
        <button className="btn btn-outline-primary btn-pill mb-4" onClick={onBack}>
          <FaArrowLeft />
          Back to collection
        </button>

        <Breadcrumbs title={product.product_name} items={breadcrumbItems} />

        <div className="product-detail">
          <div className="product-detail__gallery">
            <div id="productImageCarousel" className="carousel slide" data-bs-ride="carousel">
              <div className="carousel-inner">
                {galleryImages.map((img, index) => (
                  <div
                    key={img.product_image_id}
                    className={`carousel-item ${index === 0 ? "active" : ""}`}
                  >
                    <img
                      src={
                        img.image.startsWith("http")
                          ? img.image
                          : `${IMAGE_PATH}${img.image}`
                      }
                      className="d-block w-100"
                      alt={product.product_name}
                    />
                  </div>
                ))}
              </div>

              {galleryImages.length > 1 && (
                <>
                  <button
                    className="carousel-control-prev"
                    type="button"
                    data-bs-target="#productImageCarousel"
                    data-bs-slide="prev"
                  >
                    <span className="carousel-control-prev-icon" aria-hidden="true" />
                    <span className="visually-hidden">Previous</span>
                  </button>
                  <button
                    className="carousel-control-next"
                    type="button"
                    data-bs-target="#productImageCarousel"
                    data-bs-slide="next"
                  >
                    <span className="carousel-control-next-icon" aria-hidden="true" />
                    <span className="visually-hidden">Next</span>
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="product-detail__info">
            <div className="product-detail__header">
              <div>
                <span className="badge rounded-pill">{product.category_name}</span>
                <h1>{product.product_name}</h1>
              </div>
              <button
                className={`like-toggle ${liked ? "is-liked" : ""}`}
                onClick={(event) => onLike(product.product_id, event)}
              >
                {liked ? <FaHeart /> : <FaRegHeart />}
              </button>
            </div>

            <h2 className="product-detail__price">AED {product.price}</h2>

            <section className="product-detail__section">
              <h3>Description</h3>
              <p>{product.description}</p>
            </section>

            <section className="product-detail__section">
              <h3>Specifications</h3>
              <ul className="product-specs">
                <li>
                  <span>Brand</span>
                  <strong>{product.brand_name}</strong>
                </li>
                <li>
                  <span>Details</span>
                  <strong>{product.specification}</strong>
                </li>
              </ul>
            </section>

            <div className="product-detail__cta">
              <button className="btn btn-primary btn-lg" onClick={onToggleOwner}>
                <FaUser />
                {showOwnerDetails ? "Hide owner details" : "Show owner details"}
              </button>
            </div>

            {showOwnerDetails && (
              <section className="owner-card">
                <h3>Product owner</h3>
                <div className="owner-card__grid">
                  <OwnerInfo icon={<FaBuilding />} label="Business" value={product.business_name} />
                  <OwnerInfo icon={<FaUser />} label="Name" value={product.username} />
                  <OwnerInfo
                    icon={<FaPhone />}
                    label="Mobile"
                    value={product.mobile}
                    isLink={`tel:${product.mobile}`}
                  />
                  <OwnerInfo
                    icon={<FaEnvelope />}
                    label="Email"
                    value={product.email}
                    isLink={`mailto:${product.email}`}
                  />
                  <OwnerInfo
                    icon={<FaMapMarkerAlt />}
                    label="Location"
                    value={product.business_location}
                    wide
                  />
                </div>

                <div className="owner-card__actions">
                  <a className="btn btn-success" href={`tel:${product.mobile}`}>
                    <FaPhone />
                    Call now
                  </a>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log("Chat button clicked, onChatWithSeller:", typeof onChatWithSeller);
                      if (onChatWithSeller) {
                        onChatWithSeller(product);
                      } else {
                        console.error("onChatWithSeller function is not available");
                        alert("Chat feature is not available. Please refresh the page.");
                      }
                    }}
                  >
                    <BsChat />
                    Chat with seller
                  </button>
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
      {shouldShowFooter && <Footer token={token} />}
    </div>
  );
};

export const OwnerInfo = ({ icon, label, value, isLink, wide }) => {
  const content = isLink ? (
    <a href={isLink} className="owner-card__value owner-card__value--link">
      {value}
    </a>
  ) : (
    <span className="owner-card__value">{value}</span>
  );

  return (
    <div className={`owner-card__item ${wide ? "owner-card__item--wide" : ""}`}>
      <div className="owner-card__icon">{icon}</div>
      <div>
        <span className="owner-card__label">{label}</span>
        {content}
      </div>
    </div>
  );
};


export const CategoryHub = () => {
  const token = sessionStorage.getItem("token");
  const shouldShowFooter = !token;
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  const fetchCategory = () => {
    // setLoading(true);
    axios
      .get(`${API_URL}/get_category`)
      .then((response) => {
        if (response.data.success) {
          setCategories(response.data.categories_arr || []);
        }
      })
      .catch((error) => console.error("Error:", error))
    // .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCategory();
  }, []);

  const handleCategoryClick = (categoryId, categoryName) => {

    navigate(`/category/${categoryId}`, {
      state: { categoryName }
    });
  };

  return (
    <div className="category-hub-page">
      <div className="container">
        <div className="category-hub__grid">
          {categories.map((category) => (
            <div
              className="category-card"
              key={category.category_id}
              onClick={() => handleCategoryClick(category.category_id, category.category_name)}
              style={{ cursor: 'pointer' }}
            >
              <div className="category-card__media">
                <img src={`${IMAGE_PATH}${category.image}`} alt={category.name} />
                <span className="category-card__pill">{category.category_name}</span>
              </div>
              <div className="category-card__body">
                <h3>{category.category_name}</h3>
                <p>{category.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {shouldShowFooter && <Footer token={token} />}
    </div>
  );
};

export const Footer = ({ token }) => (
  <footer className="footer-modern">
    <div className="container">
      <div className="footer-modern__grid">
        <div>
          <div className="footer-modern__brand">
            <img src={APP_LOGO} alt="TechXchange" />
            TechXchange
          </div>
          <p>
            Premium marketplace built for modern traders. Discover authentic collections, connect
            with sellers, and grow your story.
          </p>
        </div>

        <div>
          <h5>Quick links</h5>
          <ul>
            <li>
              <a href={token ? "/" : "/login"}>Home</a>
            </li>
            <li>
              <a href={token ? "/addProduct" : "/login"}>Sell with us</a>
            </li>
            <li>
              <a href="/terms-and-conditions">Terms &amp; Conditions</a>
            </li>
            <li>
              <a href="/privacy-policy">Privacy policy</a>
            </li>
          </ul>
        </div>

        <div>
          <h5>Stay in touch</h5>
          <p>Follow us on socials and never miss a drop.</p>
          <div className="footer-modern__socials">
            <a href="https://facebook.com" aria-label="Facebook">
              <i className="bi bi-facebook" />
            </a>
            <a href="https://twitter.com" aria-label="Twitter">
              <i className="bi bi-twitter" />
            </a>
            <a href="https://www.instagram.com" aria-label="Instagram">
              <i className="bi bi-instagram" />
            </a>
            <a href="https://www.behance.net" aria-label="Behance">
              <i className="bi bi-behance" />
            </a>
          </div>
        </div>
      </div>

      <div className="footer-modern__bottom">
        <p>
          &copy; {new Date().getFullYear()} <strong>@blackcube.ae</strong>. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

export default Home;